// ============================================================
// AccessDenied — premium OLIVIUM-branded denial surface.
//
// Shown when a GitHub-authenticated user is not on the approved
// operator allowlist. Intentional, calm, and honest — this is
// not an error, it's access control working as designed.
// ============================================================

import { useAuth } from '../auth/AuthContext'

export default function AccessDenied() {
  const { user, signOut } = useAuth()

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--olv-void)',
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow — red-shifted to signal denial */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(220,38,38,0.04) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(124,92,252,0.03) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 28,
          width: 440,
          maxWidth: '100%',
        }}
      >
        {/* OLIVIUM brand mark */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(220,38,38,0.12), rgba(124,92,252,0.08))',
              border: '1px solid rgba(220,38,38,0.20)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              color: '#dc2626',
              fontFamily: 'var(--font-brand)',
              boxShadow: '0 0 32px rgba(220,38,38,0.15)',
            }}
          >
            ◈
          </div>
          <div
            style={{
              fontFamily: 'var(--font-brand)',
              fontSize: 18,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #dc2626, #7c5cfc)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.08em',
            }}
          >
            OLIVIUM
          </div>
        </div>

        {/* Denial card */}
        <div
          style={{
            width: '100%',
            background: 'var(--olv-elevated)',
            border: '1px solid rgba(220,38,38,0.15)',
            borderRadius: 16,
            padding: 32,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(220,38,38,0.06)',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-brand)',
                fontSize: 22,
                color: 'var(--olv-text-primary)',
                marginBottom: 8,
              }}
            >
              Access not granted
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--olv-text-secondary)',
                fontFamily: 'var(--font-mono)',
                lineHeight: 1.7,
              }}
            >
              Your GitHub identity was authenticated, but your account is not
              on the approved operator list for this Square Code instance.
            </div>
          </div>

          {/* Identity display */}
          <div
            style={{
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid var(--olv-panel-border)',
              borderRadius: 10,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span
                style={{
                  fontSize: 9,
                  color: 'var(--olv-text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                Authenticated as
              </span>
              <span
                style={{
                  fontSize: 9,
                  color: '#dc2626',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Not approved
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--olv-text-primary)',
                fontFamily: 'var(--font-mono)',
                wordBreak: 'break-all',
              }}
            >
              {user?.email || user?.id || 'Unknown identity'}
            </div>
          </div>

          <div
            style={{
              fontSize: 11,
              color: 'var(--olv-text-tertiary)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1.6,
            }}
          >
            Square Code is a governed private operator system. Access is
            granted by invitation only. If you believe this is an error,
            contact the platform administrator.
          </div>

          <button
            onClick={() => signOut()}
            style={{
              width: '100%',
              padding: '10px 0',
              borderRadius: 8,
              border: '1px solid var(--olv-panel-border)',
              background: 'transparent',
              color: 'var(--olv-text-secondary)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              letterSpacing: '0.06em',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            Sign out and return to login
          </button>
        </div>

        {/* Trust footer */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 9,
              color: 'var(--olv-text-tertiary)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.08em',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#dc2626', display: 'inline-block' }} />
              ACCESS DENIED
            </span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#059669', display: 'inline-block' }} />
              EVENT LOGGED
            </span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#059669', display: 'inline-block' }} />
              AUDIT TRAIL
            </span>
          </div>
          <div style={{ fontSize: 8, color: 'var(--olv-text-tertiary)', fontFamily: 'var(--font-mono)', opacity: 0.5, letterSpacing: '0.14em' }}>
            OLIVIUM TRUST LAYER · SQUARE CODE
          </div>
        </div>
      </div>
    </div>
  )
}
